import { effect, inject, Injectable } from '@angular/core'

import { ScriptLoaderService } from '../http/services/script-loader.service'
import { ErrorHandlers } from '@app/services/error-handlers.service'
import * as environment from '@app/environment'
import { SiteStore } from '@app/store/site.store'
import { TrackingNavigationService } from '@app/modules/analytics/tracking-navigation.service'

export type BurResolveResponse = string | undefined

interface BurConfig {
  domain: string
  everflowTrackingDomain?: string
}

interface Bur {
  track(): Promise<void>
  get(): BurResolveResponse
  cleanup(): void
}

type BurConstructor = new (config: BurConfig) => Bur

declare global {
  interface Window {
    bur?: BurConstructor
  }
}

@Injectable({ providedIn: 'root' })
export class AttributionService {
  private readonly siteStore = inject(SiteStore)
  private readonly scriptLoaderService = inject(ScriptLoaderService)
  private readonly trackingNavigationService = inject(TrackingNavigationService)
  private tracker: Bur | undefined = undefined
  private enabled = false

  constructor() {
    effect(() => {
      if (!this.enabled && !this.tracker && this.siteStore.features().bur) {
        this.enabled = true
        void this.init()
      }
    })
  }

  private async init(): Promise<Bur | null> {
    if (this.tracker) return this.tracker

    await this.scriptLoaderService
      .load(`https://${environment.siteDomain}/assets/lib/bur.js`)
      .catch(error => ErrorHandlers.handleError(error))

    if (window.bur) {
      this.tracker = new window.bur({ domain: environment.siteDomain })

      this.trackingNavigationService.pageView$.subscribe(() => {
        this.tracker!.track()
      })

      return this.tracker
    }

    return null
  }

  getIfResolved(): BurResolveResponse | undefined {
    return this.tracker?.get()
  }

  async cleanup(): Promise<void> {
    if (!this.enabled) return
    const tracker = await this.init()
    tracker?.cleanup()
  }
}
